import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'quill-mention';
import axiosInstance from '../../services/AxiosInstance';
import { addForumComment, updateForumComment } from '../../services/ForumService';
import { canUploadForumFiles, clearForumSubscriptionCache } from '../../utils/forumAccess';
import { toast } from 'react-toastify';
import { formatDateTimeToDDMMMYYYY } from '../../utils/dateFormatter';
import { downloadForumAttachmentFile, openForumAttachmentInNewTab } from '../../utils/forumAttachment';
import WorkRegisterTagModal from '../workRegister/WorkRegisterTagModal';
import { useHasMenuKey } from '../../hooks/useHasMenuKey';
import { getForumAssignedIndividuals, getForumAssignedOrgQuery } from './forumParticipants';
import '../tasks/mention.css';

const getDisplaySubject = (subject, content) => {
  if (subject && String(subject).trim()) return String(subject).trim();
  const plain = (content || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
  const words = plain.split(/\s+/).filter(Boolean).slice(0, 10).join(' ');
  return words || '';
};

const LoadingSpinner = ({ size = 'sm' }) => (
  <span
    className={`spinner-border spinner-border-${size}`}
    role="status"
    aria-hidden="true"
    style={{ width: size === 'sm' ? '0.85rem' : '1rem', height: size === 'sm' ? '0.85rem' : '1rem' }}
  />
);

function extractPlainText(html) {
  return (html || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
}

function countAllReplies(comment) {
  if (!comment?.replies?.length) return 0;
  return comment.replies.reduce((sum, reply) => sum + 1 + countAllReplies(reply), 0);
}

const IMAGE_EXTENSIONS = /^(png|jpg|jpeg|gif|webp|svg)$/;

function getFileIcon(ext) {
  const iconMap = {
    pdf: '📄',
    doc: '📝',
    docx: '📝',
    xls: '📊',
    xlsx: '📊',
    ppt: '📊',
    pptx: '📊',
    txt: '📃',
    zip: '🗜️',
    rar: '🗜️',
    mp4: '🎥',
    mp3: '🎵',
    csv: '📋',
  };
  return iconMap[ext] || '📎';
}

function ForumCommentFiles({ files }) {
  if (!files?.length) return null;

  const handleOpen = async (file, preferDownload = false) => {
    if (!file.id) {
      toast.error('Attachment is missing an id. Refresh the page.');
      return;
    }
    try {
      if (preferDownload) {
        await downloadForumAttachmentFile(file.id, file.file_name);
      } else {
        await openForumAttachmentInNewTab(file.id);
      }
    } catch (e) {
      toast.error(e.message || 'Could not open file');
    }
  };

  return (
    <div className="mt-2 mb-2 d-flex flex-wrap gap-2">
      {files.map((file) => {
        const fileExtension = (file.file_extension || '').toLowerCase().replace(/^\./, '');
        const isImage = IMAGE_EXTENSIONS.test(fileExtension);

        if (isImage) {
          return (
            <button
              key={file.id}
              type="button"
              className="file-attachment-card border-0 p-0 bg-white text-start"
              style={{
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                cursor: 'pointer',
                border: '1px solid #e0e0e0',
                width: '100px',
              }}
              title={`Open ${file.file_name}`}
              onClick={() => handleOpen(file, false)}
            >
              <div
                style={{
                  width: '100px',
                  height: '100px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f8f9fa',
                  fontSize: '28px',
                }}
              >
                🖼️
              </div>
              <div
                style={{
                  padding: '4px 6px',
                  borderTop: '1px solid #e0e0e0',
                  fontSize: '10px',
                  color: '#555',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {file.file_name}
              </div>
            </button>
          );
        }

        return (
          <button
            key={file.id}
            type="button"
            className="file-attachment-card border-0 p-0 bg-white text-start"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100px',
              height: '100px',
              padding: '12px',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              background: '#f8f9fa',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              textAlign: 'center',
            }}
            title={`Download ${file.file_name}`}
            onClick={() => handleOpen(file, true)}
          >
            <div style={{ fontSize: '28px', marginBottom: '6px' }}>{getFileIcon(fileExtension)}</div>
            <div
              style={{
                fontSize: '10px',
                color: '#333',
                wordBreak: 'break-word',
                lineHeight: '1.2',
                maxWidth: '100%',
              }}
            >
              {file.file_name.length > 14 ? `${file.file_name.substring(0, 14)}...` : file.file_name}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function FilePreviewList({ files, onRemove, disabled }) {
  if (!files?.length) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-2 items-start" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-start' }}>
      {files.map((file, idx) => {
        const isExistingFile = file.id !== undefined;
        const fileName = isExistingFile ? file.file_name : file.name;
        const fileExtension = isExistingFile
          ? (file.file_extension || '').toLowerCase().replace(/^\./, '')
          : '';
        const isImage = isExistingFile
          ? IMAGE_EXTENSIONS.test(fileExtension)
          : file.type?.startsWith('image/');
        const fileUrl = !isExistingFile ? URL.createObjectURL(file) : null;

        if (isImage) {
          return (
            <div key={isExistingFile ? file.id : idx} className="file-wrapper" style={{ position: 'relative', display: 'inline-flex', alignItems: 'flex-start' }}>
              {isExistingFile ? (
                <div
                  style={{
                    width: '100px',
                    height: '100px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f8f9fa',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    fontSize: '28px',
                  }}
                >
                  🖼️
                </div>
              ) : (
                <img
                  src={fileUrl}
                  alt={fileName}
                  style={{ width: '100px', height: '100px', objectFit: 'cover', cursor: 'pointer' }}
                  className="rounded border"
                  onClick={() => window.open(fileUrl, '_blank')}
                />
              )}
              <button
                type="button"
                className="delete-btn"
                onClick={() => onRemove(idx)}
                disabled={disabled}
                style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  background: disabled ? '#ccc' : '#ff4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {disabled ? <LoadingSpinner size="sm" /> : '✕'}
              </button>
            </div>
          );
        }

        return (
          <div key={isExistingFile ? file.id : idx} className="file-wrapper" style={{ position: 'relative', display: 'inline-flex', alignItems: 'flex-start' }}>
            <div
              style={{
                color: '#2A3335',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                background: '#f9f9f9',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                height: '100px',
                width: '100px',
                flexDirection: 'column',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  background: '#666',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '16px',
                  marginBottom: '4px',
                }}
              >
                {isExistingFile ? getFileIcon(fileExtension) : '📄'}
              </div>
              <span style={{ fontSize: '10px', wordBreak: 'break-word', lineHeight: '1.2' }}>
                {fileName.length > 15 ? `${fileName.substring(0, 15)}...` : fileName}
              </span>
            </div>
            <button
              type="button"
              className="delete-btn"
              onClick={() => onRemove(idx)}
              disabled={disabled}
              style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                background: disabled ? '#ccc' : '#ff4444',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {disabled ? <LoadingSpinner size="sm" /> : '✕'}
            </button>
          </div>
        );
      })}
    </div>
  );
}

function CommentComposer({
  subject,
  onSubjectChange,
  editorContent,
  onEditorChange,
  quillRef,
  subjectInputRef,
  modules,
  commentError,
  uploadedFile,
  onAddFiles,
  onRemoveFile,
  isSubmitting,
  onSubmit,
  onCancel,
  submitLabel,
  quillPlaceholder,
  subjectPlaceholder,
  isReply = false,
}) {
  return (
    <div className={`border rounded p-2 ${isReply ? 'border-green-300 bg-green-50' : 'border-gray-300'}`}>
      <input
        ref={subjectInputRef}
        type="text"
        className="form-control mb-2"
        placeholder={subjectPlaceholder}
        value={subject}
        onChange={(e) => onSubjectChange(e.target.value)}
        maxLength={500}
        style={{ fontSize: '0.9rem' }}
      />
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={editorContent}
        onChange={onEditorChange}
        modules={modules}
        placeholder={quillPlaceholder}
        className="mb-2"
      />
      {commentError && <div className="text-danger small mt-1 mb-2">{commentError}</div>}

      <div className="file-upload-section mt-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
        <div className="flex items-center justify-between mb-2">
          <button
            type="button"
            onClick={onAddFiles}
            disabled={isSubmitting}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-normal text-gray-500 bg-gray-100 border border-gray-200 rounded hover:bg-gray-200 hover:text-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-400 transition-all duration-150"
            style={{
              opacity: isSubmitting ? 0.6 : 1,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
            }}
          >
            {isSubmitting && <LoadingSpinner size="sm" />}
            + Add Files
          </button>
        </div>
        <FilePreviewList files={uploadedFile} onRemove={onRemoveFile} disabled={isSubmitting} />
      </div>

      <div className="forum-comment-composer-actions flex gap-2 items-center" style={{ display: 'flex', marginTop: '20px' }}>
        <button
          type="button"
          className="submit-button"
          onClick={onSubmit}
          disabled={isSubmitting}
          style={{
            opacity: isSubmitting ? 0.6 : 1,
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            minWidth: '100px',
            justifyContent: 'center',
          }}
        >
          {isSubmitting && <LoadingSpinner size="sm" />}
          {isSubmitting
            ? `${submitLabel === 'Update' ? 'Updating' : 'Submitting'}...`
            : submitLabel}
        </button>
        <button
          type="button"
          className="cancel-button"
          onClick={onCancel}
          disabled={isSubmitting}
          style={{
            opacity: isSubmitting ? 0.6 : 1,
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            minWidth: '80px',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function ForumCommentSection({
  forumId,
  forum = null,
  comments = [],
  onCommentAdded,
  onCreateTask,
  disabled = false,
  scrollToCommentId = null,
}) {
  const { hasKey: hasWorkRegisterAccess } = useHasMenuKey('work_register');
  const { hasKey: hasTaskListAccess } = useHasMenuKey('task_list');
  const quillRef = useRef(null);
  const subjectInputRef = useRef(null);
  const [users, setUsers] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const [subject, setSubject] = useState('');
  const [mode, setMode] = useState('comment');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [parentId, setParentId] = useState(null);
  const [quoteId, setQuoteId] = useState(null);
  const [uploadedFile, setUploadedFile] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [commentError, setCommentError] = useState('');
  const [combinedComments, setCombinedComments] = useState([]);
  const [collapsedThreads, setCollapsedThreads] = useState(() => new Set());
  const [wrTagCommentId, setWrTagCommentId] = useState(null);
  const [currentCorporateUserId, setCurrentCorporateUserId] = useState(null);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentCorporateUserId(userData?.id || null);
    if (!userData?.corporateId || !forum) {
      setUsers([]);
      return;
    }

    const currentUserId = Number(userData.id);
    const withoutSelf = (list) =>
      (list || []).filter((user) => Number(user.id) !== currentUserId && user.full_name);
    const orgQuery = getForumAssignedOrgQuery(forum);
    let cancelled = false;

    const applyUsers = (list) => {
      if (!cancelled) setUsers(withoutSelf(list));
    };

    if (!orgQuery) {
      applyUsers(getForumAssignedIndividuals(forum));
      return () => {
        cancelled = true;
      };
    }

    const params = new URLSearchParams();
    params.append('corporateId', parseInt(userData.corporateId, 10));
    Object.entries(orgQuery).forEach(([key, value]) => params.append(key, value));

    axiosInstance
      .get(`/list/corporateuserslist?${params.toString()}`)
      .then((res) => {
        const byId = new Map();
        getForumAssignedIndividuals(forum).forEach((user) => byId.set(Number(user.id), user));
        (res.data.users || []).forEach((user) => {
          if (user?.id && user?.full_name) {
            byId.set(Number(user.id), { id: user.id, full_name: user.full_name });
          }
        });
        applyUsers(Array.from(byId.values()));
      })
      .catch(() => applyUsers(getForumAssignedIndividuals(forum)));

    return () => {
      cancelled = true;
    };
  }, [forum]);

  useEffect(() => {
    const processComments = (commentList) =>
      commentList.map((comment) => {
        const userName = comment.CorporateUser?.full_name || 'Unknown';
        const processed = {
          commentId: comment.id,
          subject: getDisplaySubject(comment.subject, comment.content),
          originalSubject: comment.subject || '',
          content: comment.content || '',
          createdAt: formatDateTimeToDDMMMYYYY(comment.created_at, true),
          updatedAt: comment.updated_at
            ? formatDateTimeToDDMMMYYYY(comment.updated_at, true)
            : null,
          userName,
          userId: comment.user_id,
          isClosed: !!comment.is_closed,
          quotedComment: comment.quotedComment,
          files: comment.files || [],
          taskCount: comment.task_count ?? (comment.linked_tasks?.length || 0),
          raw: comment,
        };
        if (comment.replies?.length) {
          processed.replies = processComments(comment.replies);
        }
        return processed;
      });

    setCombinedComments(processComments(comments));
  }, [comments]);

  const findRootThreadId = useCallback((commentList, targetId) => {
    for (const comment of commentList) {
      if (String(comment.commentId) === String(targetId)) {
        return comment.commentId;
      }
      const containsTarget = (replies) => {
        for (const reply of replies) {
          if (String(reply.commentId) === String(targetId)) return true;
          if (reply.replies?.length && containsTarget(reply.replies)) return true;
        }
        return false;
      };
      if (comment.replies?.length && containsTarget(comment.replies)) {
        return comment.commentId;
      }
    }
    return null;
  }, []);

  useEffect(() => {
    if (!scrollToCommentId || combinedComments.length === 0) return;

    const rootThreadId = findRootThreadId(combinedComments, scrollToCommentId);
    if (rootThreadId) {
      setCollapsedThreads((prev) => {
        if (!prev.has(rootThreadId)) return prev;
        const next = new Set(prev);
        next.delete(rootThreadId);
        return next;
      });
    }

    const scrollTimer = setTimeout(() => {
      const el = document.getElementById(`comment-${scrollToCommentId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('comment-highlight');
        setTimeout(() => el.classList.remove('comment-highlight'), 2000);
      }
    }, 300);

    return () => clearTimeout(scrollTimer);
  }, [scrollToCommentId, combinedComments, findRootThreadId]);

  const mentionValues = useMemo(
    () =>
      users
        .filter((user) => user?.id && user?.full_name)
        .map((user) => ({ id: user.id, value: user.full_name })),
    [users]
  );

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ align: [] }],
        ],
      },
      mention: {
        mentionDenotationChars: ['@'],
        source(searchTerm, renderList) {
          const matches = mentionValues.filter((item) =>
            item.value.toLowerCase().includes(searchTerm.toLowerCase())
          );
          renderList(matches, searchTerm);
        },
        renderItem(item) {
          return `<div><strong>${item.value}</strong></div>`;
        },
      },
    }),
    [mentionValues]
  );

  const resetComposer = () => {
    setCommentError('');
    setSubject('');
    setEditorContent('');
    setReplyingTo(null);
    setEditingCommentId(null);
    setParentId(null);
    setQuoteId(null);
    setMode('comment');
    setExpanded(false);
    setUploadedFile([]);
  };

  const handleAddComment = () => {
    if (disabled) return;
    setMode('comment');
    setEditingCommentId(null);
    setReplyingTo(null);
    setParentId(null);
    setQuoteId(null);
    setSubject('');
    setEditorContent('');
    setUploadedFile([]);
    setExpanded(true);
    setTimeout(() => subjectInputRef.current?.focus(), 100);
  };

  const handleReply = (comment) => {
    if (disabled || comment.isClosed) return;
    setMode('reply');
    setEditingCommentId(null);
    setReplyingTo(comment.commentId);
    setParentId(comment.commentId);
    setQuoteId(comment.commentId);
    setSubject('');
    setEditorContent(
      `<p><span style="background:#e0f7ff; color:#007bff;">@${comment.userName}</span>&nbsp;</p><p><br/></p>`
    );
    setUploadedFile([]);
    setExpanded(false);
    setTimeout(() => subjectInputRef.current?.focus(), 100);
  };

  const handleEdit = (comment) => {
    if (disabled || comment.isClosed) return;
    setMode('edit');
    setEditingCommentId(comment.commentId);
    setReplyingTo(null);
    setParentId(null);
    setQuoteId(null);
    setSubject(comment.raw?.subject || comment.originalSubject || '');
    setEditorContent(comment.content || '');
    setUploadedFile([...(comment.files || [])]);
    setExpanded(false);
    setTimeout(() => subjectInputRef.current?.focus(), 100);
  };

  const handleFileUpload = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', '*/*');
    input.setAttribute('multiple', 'true');
    input.value = '';
    input.click();
    input.onchange = () => {
      const files = Array.from(input.files || []);
      if (files.length) setUploadedFile((prev) => [...prev, ...files]);
    };
  };

  const handleRemoveFile = (index) => {
    setUploadedFile((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEditorChange = (content) => {
    setEditorContent(content);
    if (commentError) setCommentError('');
  };

  const submitEdit = async () => {
    const plainContent = extractPlainText(editorContent);
    if (!plainContent && !subject.trim()) {
      setCommentError('Comment is required');
      return;
    }
    setCommentError('');
    setIsSubmitting(true);
    try {
      const existingFiles = uploadedFile.filter((file) => file.id !== undefined);
      const newFiles = uploadedFile.filter((file) => file.id === undefined);
      const requiredBytes = newFiles.reduce((sum, f) => sum + (f.size || 0), 0);
      if (requiredBytes > 0) {
        const uploadCheck = await canUploadForumFiles(requiredBytes);
        if (!uploadCheck.canUpload) {
          toast.error(uploadCheck.message || 'Insufficient forum storage');
          setIsSubmitting(false);
          return;
        }
      }

      const fd = new FormData();
      fd.append('subject', subject.trim());
      fd.append('content', editorContent || '');
      fd.append('existing_files', JSON.stringify(existingFiles.map((file) => file.id)));
      newFiles.forEach((file) => fd.append('files', file));

      const res = await updateForumComment(editingCommentId, fd);
      if (res.data?.success === false) {
        toast.error(res.data.message || 'Failed to update comment');
        return;
      }

      clearForumSubscriptionCache();
      toast.success('Comment updated');
      resetComposer();
      onCommentAdded?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitComment = async () => {
    if (mode === 'edit') {
      await submitEdit();
      return;
    }

    const plainContent = extractPlainText(editorContent);
    if (!plainContent && !subject.trim()) {
      setCommentError('Comment is required');
      return;
    }
    setCommentError('');
    setIsSubmitting(true);
    try {
      const requiredBytes = uploadedFile.reduce((sum, f) => sum + (f.size || 0), 0);
      if (requiredBytes > 0) {
        const uploadCheck = await canUploadForumFiles(requiredBytes);
        if (!uploadCheck.canUpload) {
          toast.error(uploadCheck.message || 'Insufficient forum storage');
          setIsSubmitting(false);
          return;
        }
      }

      const fd = new FormData();
      if (subject.trim()) fd.append('subject', subject.trim());
      fd.append('content', editorContent || '');
      if (parentId) fd.append('parent_id', parentId);
      if (quoteId) fd.append('quoted_comment_id', quoteId);
      uploadedFile.forEach((file) => fd.append('files', file));

      const res = await addForumComment(forumId, fd);
      if (res.data?.success === false) {
        toast.error(res.data.message || 'Failed to post comment');
        return;
      }

      clearForumSubscriptionCache();
      toast.success(mode === 'reply' ? 'Reply posted' : 'Comment posted');
      resetComposer();
      onCommentAdded?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to post comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCommentNode = (comment, isReply = false, options = {}) => {
    const {
      hideReplies = false,
      onToggleCollapse = null,
      isCollapsed = false,
      replyCount = 0,
    } = options;

    const handleUpperSectionToggle = () => {
      if (onToggleCollapse) onToggleCollapse();
    };

    const handleUpperSectionKeyDown = (e) => {
      if (!onToggleCollapse) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onToggleCollapse();
      }
    };

    const isEditingThis = mode === 'edit' && editingCommentId === comment.commentId;
    const isEdited = comment.updatedAt && comment.createdAt && comment.updatedAt !== comment.createdAt;

    return (
    <div
      key={comment.commentId}
      id={`comment-${comment.commentId}`}
      className={isReply ? 'comment-reply-item' : 'comment-item'}
    >
      <div className="comment-body">
        <div
          className={`forum-comment-upper${onToggleCollapse ? ' forum-comment-upper--clickable' : ''}`}
          onClick={onToggleCollapse ? handleUpperSectionToggle : undefined}
          onKeyDown={onToggleCollapse ? handleUpperSectionKeyDown : undefined}
          role={onToggleCollapse ? 'button' : undefined}
          tabIndex={onToggleCollapse ? 0 : undefined}
          aria-expanded={onToggleCollapse ? !isCollapsed : undefined}
          aria-controls={onToggleCollapse ? `forum-thread-replies-${comment.commentId}` : undefined}
          aria-label={onToggleCollapse ? (isCollapsed ? 'Show replies' : 'Hide replies') : undefined}
        >
          <div className="comment-header">
            <div className="forum-comment-header__main">
              <div className="comment-avatar comment-avatar-inline">
                {comment.userName?.charAt(0).toUpperCase()}
              </div>
              <span className="comment-username">{comment.userName}</span>
              <span className="comment-timestamp">{comment.createdAt}</span>
              {isEdited && <span className="comment-edited">Edited {comment.updatedAt}</span>}
              {comment.isClosed && <span className="comment-edited">closed</span>}
              {comment.taskCount > 0 && (
                <span className="forum-comment-task-count">
                  <i className="fas fa-tasks" aria-hidden></i>
                  {comment.taskCount} {comment.taskCount === 1 ? 'task' : 'tasks'}
                </span>
              )}
            </div>
            {onToggleCollapse && (
              <div className="forum-comment-group__toggle-visual" aria-hidden>
                <span className="forum-comment-group__reply-count">
                  {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                </span>
                <i
                  className={`fas ${isCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'} forum-comment-group__chevron`}
                />
              </div>
            )}
          </div>

          {comment.originalSubject && (
            <div className="comment-subject mb-1" style={{ fontWeight: 600, color: '#333', fontSize: '0.95rem' }}>
              {comment.originalSubject}
            </div>
          )}
        </div>

        {comment.quotedComment && !isEditingThis && (
          <blockquote
            className="forum-post-quote mb-2"
            style={{ fontSize: '12px', color: '#787c7e', borderLeft: '3px solid #C42126', paddingLeft: '8px' }}
          >
            {comment.quotedComment.CorporateUser?.full_name}:{' '}
            {comment.quotedComment.subject || extractPlainText(comment.quotedComment.content).slice(0, 100)}
          </blockquote>
        )}

        {isEditingThis ? (
          <div className="mt-2 forum-comment-edit-composer">
            <CommentComposer
              subject={subject}
              onSubjectChange={setSubject}
              editorContent={editorContent}
              onEditorChange={handleEditorChange}
              quillRef={quillRef}
              subjectInputRef={subjectInputRef}
              modules={modules}
              commentError={commentError}
              uploadedFile={uploadedFile}
              onAddFiles={handleFileUpload}
              onRemoveFile={handleRemoveFile}
              isSubmitting={isSubmitting}
              onSubmit={submitComment}
              onCancel={resetComposer}
              submitLabel="Update"
              quillPlaceholder="Edit comment..."
              subjectPlaceholder="Subject (optional)"
              isReply={isReply}
            />
          </div>
        ) : (
          <>
            <div className="comment-content" dangerouslySetInnerHTML={{ __html: comment.content }} />

            <ForumCommentFiles files={comment.files} />

            <div className="comment-actions-bar">
              {!disabled && !comment.isClosed && (
                <button type="button" className="comment-action-btn" onClick={() => handleReply(comment)}>
                  <i className="far fa-comment" aria-hidden></i>
                  Reply
                </button>
              )}
              {!disabled && !comment.isClosed && currentCorporateUserId === comment.userId && (
                <button type="button" className="comment-action-btn" onClick={() => handleEdit(comment)}>
                  <i className="far fa-edit" aria-hidden></i>
                  Edit
                </button>
              )}
              {hasTaskListAccess && (
                <button type="button" className="comment-action-btn" onClick={() => onCreateTask?.(comment.raw)}>
                  <i className="fas fa-tasks" aria-hidden></i>
                  Create task
                </button>
              )}
              {currentCorporateUserId && hasWorkRegisterAccess && (
                <button
                  type="button"
                  className="comment-action-btn"
                  onClick={() => setWrTagCommentId(comment.commentId)}
                  aria-label="Add to Work Register"
                  title="Add to Work Register"
                >
                  <i className="far fa-bell" aria-hidden></i>
                  Work Register
                </button>
              )}
            </div>
          </>
        )}

        {replyingTo === comment.commentId && mode === 'reply' && (
          <div className="mt-3 ml-0 forum-comment-reply-composer">
            <CommentComposer
              subject={subject}
              onSubjectChange={setSubject}
              editorContent={editorContent}
              onEditorChange={handleEditorChange}
              quillRef={quillRef}
              subjectInputRef={subjectInputRef}
              modules={modules}
              commentError={commentError}
              uploadedFile={uploadedFile}
              onAddFiles={handleFileUpload}
              onRemoveFile={handleRemoveFile}
              isSubmitting={isSubmitting}
              onSubmit={submitComment}
              onCancel={resetComposer}
              submitLabel="Submit Reply"
              quillPlaceholder="Write a reply..."
              subjectPlaceholder="Subject (optional)"
              isReply
            />
          </div>
        )}
      </div>

      {!hideReplies && comment.replies?.length > 0 && (
        <div
          id={!isReply ? `forum-thread-replies-${comment.commentId}` : undefined}
          className="comment-replies-wrapper forum-comment-replies"
        >
          {comment.replies.map((reply) => renderCommentNode(reply, true))}
        </div>
      )}
    </div>
    );
  };

  const toggleThreadCollapse = (threadId) => {
    setCollapsedThreads((prev) => {
      const next = new Set(prev);
      if (next.has(threadId)) {
        next.delete(threadId);
      } else {
        next.add(threadId);
      }
      return next;
    });
  };

  const renderCommentThread = (comment) => {
    const isCollapsed = collapsedThreads.has(comment.commentId);
    const replyCount = countAllReplies(comment);
    const hasReplies = replyCount > 0;

    return (
      <div
        key={comment.commentId}
        className={`forum-comment-group${isCollapsed ? ' forum-comment-group--collapsed' : ''}`}
      >
        {renderCommentNode(comment, false, {
          hideReplies: isCollapsed,
          isCollapsed,
          replyCount,
          onToggleCollapse: hasReplies
            ? () => toggleThreadCollapse(comment.commentId)
            : null,
        })}
      </div>
    );
  };

  return (
    <div className="comment-thread forum-comment-thread space-y-4">
      {disabled ? (
        <p className="text-muted mb-3">This discussion is closed. No new comments.</p>
      ) : (
        <>
          {!expanded && !replyingTo && !editingCommentId && (
            <div className="comment-add-placeholder" onClick={handleAddComment} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}>
              Add a comment...
            </div>
          )}

          {expanded && !replyingTo && !editingCommentId && (
            <div className="mb-4">
              <CommentComposer
                subject={subject}
                onSubjectChange={setSubject}
                editorContent={editorContent}
                onEditorChange={handleEditorChange}
                quillRef={quillRef}
                subjectInputRef={subjectInputRef}
                modules={modules}
                commentError={commentError}
                uploadedFile={uploadedFile}
                onAddFiles={handleFileUpload}
                onRemoveFile={handleRemoveFile}
                isSubmitting={isSubmitting}
                onSubmit={submitComment}
                onCancel={resetComposer}
                submitLabel="Submit"
                quillPlaceholder="Comment..."
                subjectPlaceholder="Transaction statement / Subject (optional)"
              />
            </div>
          )}
        </>
      )}

      <div className="comment-list forum-comment-list">
        {combinedComments.length === 0 ? (
          <p className="text-muted">No comments yet.</p>
        ) : (
          combinedComments.map((comment) => renderCommentThread(comment))
        )}
      </div>

      {hasWorkRegisterAccess && (
        <WorkRegisterTagModal
          show={!!wrTagCommentId}
          commentId={wrTagCommentId}
          onHide={() => setWrTagCommentId(null)}
        />
      )}
    </div>
  );
}
